/**
 * Where a course, a module or a lesson stands among its siblings (gh #5003, Sync rules: the files'
 * order), and the position writes that move the server's order to the files'.
 *
 * Pure: lists of ids in, lists of ids out.
 *
 * THE FILES' ORDER of a parent is the children its file lists, in list order. A server child the
 * files name nowhere — created in the web since the last pull, or left out of the list — is no
 * part of it: push writes the listed children's order and leaves such a child where it is, after
 * the listed sibling it follows on the server (`mergeOrder`), as everything the files do not name
 * is left as is (Decision 6). So a list that leaves a server child out is a no-op, and never a
 * perpetual change. A listed entry with no id yet is a create: it keeps its place in the list and
 * has no place in the order until the server answers its id.
 *
 * THE SERVER'S PLACEMENT is deterministic (`placeAt` in the backend's `community-do/courses.ts`):
 * a create lands last; a position write removes the child and inserts it at `min(position, n)`,
 * the others keeping their order — also into another module, for a lesson moved there. `placeAt`
 * here is the same operation, so the CLI computes every parent's new order from the writes it
 * sends, and records it as soon as each one returns.
 */

/** A listed child: the file's own entry, and its id once it has one. */
export interface Listed<E> {
  entry: E;
  id: string | undefined;
}

/** One place in a merged list: a listed entry, or a server child the files name nowhere. */
export type Slot<E> = { entry: E } | { untracked: string };

/** The ids of `entries` that have one. */
export function idsOf(entries: ReadonlyArray<{ id?: string }>): Set<string> {
  return new Set(entries.flatMap((entry) => (entry.id === undefined ? [] : [entry.id])));
}

/** The children of `order` that are in `ids`, in `order`'s order. */
export function restrict(order: readonly string[], ids: Iterable<string>): string[] {
  const keep = new Set(ids);
  return order.filter((id) => keep.has(id));
}

/**
 * A parent's list merged with the server's order of its children (`server`): `listed` in list
 * order, each child of `server` that is not in `named` (the ids the files name anywhere in the
 * parent's scope — another module of the course, for a lesson) placed after the listed sibling it
 * follows on the server, or first when it follows none. Children named elsewhere are left out:
 * the files move them away.
 */
export function mergeOrder<E>(listed: ReadonlyArray<Listed<E>>, server: readonly string[], named: ReadonlySet<string>): Array<Slot<E>> {
  const listedIds = idsOf(listed);
  const first: string[] = [];
  const after = new Map<string, string[]>();
  let anchor: string | null = null;
  for (const id of server) {
    if (listedIds.has(id)) {
      anchor = id;
    } else if (!named.has(id)) {
      if (anchor === null) first.push(id);
      else after.set(anchor, [...(after.get(anchor) ?? []), id]);
    }
  }
  const slots: Array<Slot<E>> = first.map((id) => ({ untracked: id }));
  for (const child of listed) {
    slots.push({ entry: child.entry });
    if (child.id !== undefined) for (const id of after.get(child.id) ?? []) slots.push({ untracked: id });
  }
  return slots;
}

/** The files' order (`mergeOrder`) as ids: creates have none yet. */
export function filesOrder<E>(listed: ReadonlyArray<Listed<E>>, server: readonly string[], named: ReadonlySet<string>): string[] {
  const byEntry = new Map(listed.map((child) => [child.entry, child.id]));
  return mergeOrder(listed, server, named).flatMap((slot) => {
    const id = 'untracked' in slot ? slot.untracked : byEntry.get(slot.entry);
    return id === undefined ? [] : [id];
  });
}

/** The server's placement: `id` removed from `list` (when there) and inserted at `min(position, n)`. */
export function placeAt(list: string[], id: string, position: number): void {
  const at = list.indexOf(id);
  if (at >= 0) list.splice(at, 1);
  list.splice(Math.min(position, list.length), 0, id);
}

export function sameOrder(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id, i) => id === b[i]);
}

/** One position write: put `id` at `position` among the parent's children. */
export interface Move {
  id: string;
  position: number;
}

/**
 * The fewest position writes that order `current` — one parent's children on the server — as
 * `target`. A child of `target` absent from `current` moves in (a lesson from another module); a
 * child of `current` absent from `target` is moving away, and keeps its place until it does.
 *
 * The children already in order (a longest run of `target`'s children in increasing `current`
 * positions) stay; every other one is placed, in `target`'s order, right after its predecessor in
 * `target` (first, for the first). Each write leaves every child placed so far ahead of the ones
 * still to come, so the last one leaves `target`'s children exactly in `target`'s order.
 */
export function reorderMoves(current: readonly string[], target: readonly string[]): Move[] {
  const list = [...current];
  const present = target.filter((id) => list.includes(id));
  const stay = longestRising(present, present.map((id) => list.indexOf(id)));
  const moves: Move[] = [];
  target.forEach((id, i) => {
    if (stay.has(id)) return;
    const others = list.filter((other) => other !== id);
    const position = i === 0 ? 0 : others.indexOf(target[i - 1]) + 1;
    moves.push({ id, position });
    placeAt(list, id, position);
  });
  const placed = list.filter((id) => target.includes(id));
  if (!sameOrder(placed, target)) {
    throw new Error(`reorderMoves: ${JSON.stringify(current)} → ${JSON.stringify(target)} left ${JSON.stringify(placed)}`);
  }
  return moves;
}

/** The ids of a longest strictly rising subsequence of `positions` (ties: the earliest). */
function longestRising(ids: readonly string[], positions: readonly number[]): Set<string> {
  const length = positions.map(() => 1);
  const previous = positions.map(() => -1);
  let best = -1;
  positions.forEach((position, i) => {
    for (let j = 0; j < i; j++) {
      if (positions[j] < position && length[j] + 1 > length[i]) {
        length[i] = length[j] + 1;
        previous[i] = j;
      }
    }
    if (best === -1 || length[i] > length[best]) best = i;
  });
  const stay = new Set<string>();
  for (let i = best; i >= 0; i = previous[i]) stay.add(ids[i]);
  return stay;
}
